import type { MutableRefObject } from "react";
import type { RichTextRkpiSave } from "@library-elements/text";
import { animateRkpi, getRkpiDurationFromSpeed } from "../../../utils/rkpi";
import { restoreLastRange } from "../selection-manager";
import type { FroalaEditorInstance } from "../types";

export const handleSaveRkpi = (
  editor: FroalaEditorInstance,
  data: RichTextRkpiSave,
  lastRange: MutableRefObject<Range | null>,
  refresh: () => void,
) => {
  const sel = window.getSelection();
  if (!sel) return;

  const range = restoreLastRange(lastRange, sel);
  if (!range) return;

  const { to, speed, prefix, suffix, decimal = false } = data;
  const from = 0;
  const setAttrs = (span: HTMLElement) => {
    span.contentEditable = "false";
    span.setAttribute("data-rkpi-active", "true");
    span.setAttribute("data-rkpi-number-to", to.toString());
    span.setAttribute("data-rkpi-number-from", from.toString());
    span.setAttribute("data-rkpi-prefix", prefix);
    span.setAttribute("data-rkpi-suffix", suffix);
    span.setAttribute("data-rkpi-speed", speed);
    span.setAttribute("data-rkpi-decimal", decimal.toString());
    span.innerText = `${prefix}${from}${suffix}`;
  };

  if (range.commonAncestorContainer instanceof HTMLElement) {
    let shouldBreak = false;
    const allRkpis = range.commonAncestorContainer.querySelectorAll(
      "[data-rkpi-active]",
    );
    for (const rkpi of Array.from(allRkpis)) {
      if (sel.containsNode(rkpi, true)) {
        shouldBreak = true;
        setAttrs(rkpi as HTMLElement);
        animateRkpi(
          rkpi,
          from,
          to,
          getRkpiDurationFromSpeed(speed),
          0,
          prefix,
          suffix,
          decimal,
        );
      }
    }
    if (shouldBreak) {
      editor.save.save();
      refresh();
      return;
    }
  }

  const rkpiSpan = document.createElement("span");
  setAttrs(rkpiSpan);
  range.deleteContents();
  range.insertNode(rkpiSpan);

  editor.save.save();
  refresh();

  animateRkpi(
    rkpiSpan,
    from,
    to,
    getRkpiDurationFromSpeed(speed),
    0,
    prefix,
    suffix,
    decimal,
  );
  // Reach the wrapper through this editor's own box rather than
  // `#froala-editor-${id}`: that id is not unique when an entity is rendered in
  // more than one place, so a selector can target another instance's wrapper.
  editor.$box?.find(".fr-wrapper")?.removeClass("show-placeholder");
};

export const handleDeleteRkpi = (
  editor: FroalaEditorInstance,
  lastRange: MutableRefObject<Range | null>,
  refresh: () => void,
) => {
  if (!lastRange.current) return;
  const range = lastRange.current;

  const selection = window.getSelection();
  if (!selection || !range) return;

  if (!(range.commonAncestorContainer instanceof HTMLElement)) return;

  selection.removeAllRanges();
  selection.addRange(range);

  const rkpis = range.commonAncestorContainer.querySelectorAll(
    "[data-rkpi-active]",
  );
  for (const rkpi of Array.from(rkpis)) {
    if (selection.containsNode(rkpi, true)) {
      rkpi.remove();
    }
  }

  editor.save.save();
  refresh();
};
